/**
 * Required single-select company field for new-record forms on S-pattern
 * entities (invoices, bills, payments, journal entries, etc.).
 *
 * - Hidden when the tenant has a single company (single value is implicit).
 * - Pre-filled by callers from the per-module sticky picker
 *   (`useActiveCompany().activeCompanyId`). When the sticky picker is set to
 *   "All companies" the form-level value starts empty and the field surfaces
 *   a "pick a company" hint so submission blocks until the user picks one
 *   explicitly. The form's submit guard is the caller's responsibility
 *   (`disabled={!companyId}`); this component just enforces the visible
 *   prompt.
 */
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useMyCompanies } from '@/shared/users/hooks/useCompanies';

export interface CompanyFormFieldProps {
  /** Selected company id; empty string = no selection (form blocks submit). */
  value: string;
  onChange: (companyId: string) => void;
  /** Override the label (default: "Company"). */
  label?: string;
  /** Show an `*` after the label and `aria-required`. Default true. */
  required?: boolean;
  /** Whether the field is disabled. */
  disabled?: boolean;
  /** Field id for label association. */
  id?: string;
  className?: string;
}

export function CompanyFormField({
  value,
  onChange,
  label,
  required = true,
  disabled,
  id = 'company-field',
  className,
}: CompanyFormFieldProps) {
  const { t } = useTranslation(['user_management', 'common']);
  const { data, isLoading } = useMyCompanies();
  const companies = data?.data ?? [];

  if (companies.length <= 1) return null;

  const resolvedLabel = label ?? t('user_management:companies.fieldLabel', 'Company');

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={id}>
        {resolvedLabel}
        {required && <span className="text-destructive ms-0.5" aria-hidden="true">*</span>}
      </Label>
      <Select
        value={value || ''}
        onValueChange={onChange}
        disabled={disabled || isLoading}
      >
        <SelectTrigger id={id} aria-required={required} aria-label={resolvedLabel}>
          <span className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
            <SelectValue
              placeholder={t(
                'user_management:companies.pickCompanyPlaceholder',
                'Select a company…',
              )}
            />
          </span>
        </SelectTrigger>
        <SelectContent>
          {companies.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              {c.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
